import { inject, Injectable } from "@angular/core";
import { TreeNode } from "primeng/api";
import { NamedPromptCategoryService } from "src/app/common/services/named-prompt-category.service";
import { PredefinedPrompt, PromptModel } from "src/app/modules/prompts/models/prompt-template.model";

@Injectable({
  providedIn: "root",
})
export class PromptTreeService {
  readonly namedPromptCategoryService = inject(NamedPromptCategoryService);

  buildHierarchy({
    models,
    parentId = null,
    visited = new Set<string>(),
    depth = 0,
    maxDepth = 1,
    onlyCategories = false,
  }: {
    models: PromptModel[];
    parentId?: string | null;
    visited?: Set<string>;
    depth?: number;
    maxDepth?: number;
    onlyCategories?: boolean;
  }): TreeNode<PredefinedPrompt>[] {
    return models
      .filter((model) => (!parentId && !model.parentId) || parentId === model.parentId)
      .sort((a, b) => (a.order <= b.order ? -1 : 1))
      .map((model) => this.toTreeNode(visited, model, depth, maxDepth, models, onlyCategories))
      .filter((item) => item !== null) as unknown as TreeNode<PredefinedPrompt>[];
  }

  private toTreeNode(
    visited: Set<string>,
    model: PromptModel,
    depth: number,
    maxDepth: number,
    models: PromptModel[],
    onlyCategories: boolean,
  ): TreeNode<Partial<PredefinedPrompt>> | null {
    if (visited.has(model.id)) {
      console.warn(`Circular reference detected: ${model.id}`);
      return null;
    }
    visited.add(model.id);

    let children: TreeNode<PredefinedPrompt>[] = [];
    if (depth < maxDepth) {
      children = this.buildHierarchy({
        models,
        parentId: model.id,
        visited,
        depth: depth + 1,
        maxDepth,
        onlyCategories,
      });
    }

    let predefinedChildren: object[] = [];
    if (!onlyCategories) {
      predefinedChildren = model.predefinedPrompts
        .sort((a, b) => (a.order <= b.order ? -1 : 1))
        .map((predefinedPrompt) => ({
          label: this.namedPromptCategoryService.getPromptLabel(predefinedPrompt.label),
          key: predefinedPrompt.id,
          data: { ...predefinedPrompt, label: predefinedPrompt.label, isFavorite: false },
        }));
    }

    return {
      label: this.namedPromptCategoryService.getPromptLabel(model.category),
      key: model.id,
      data: { ...model, depth, label: model.category, isFavorite: false },
      selectable: onlyCategories,
      children: [...children, ...predefinedChildren],
    };
  }
}
